import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { IoPlayOutline } from 'react-icons/io5';

import { IClient } from '~/types/client';
import { useGames } from '~/hooks/models/useGame';
import { useFullscreen } from '~/hooks/useFullscreen';
import { useLocalStorageSelect } from '~/hooks/useLocalStorageSelect';
import { compareGamesCatalogOrder } from '~/config/global.config';

import { Select } from '~/components/ui/select';
import { Button } from '~/components/ui/button';
import { GameCard } from '~/components/design/cards/GameCard';
import { LoadingPage } from '~/pages/LoadingPage';

import styles from './HomeBox.module.css';

export type HomeBoxProps = {
    client: IClient;
};

export function HomeBox({ client }: HomeBoxProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { isFullscreen, toggleFullscreen } = useFullscreen();

    const { currentValue, currentOption, options, onChange } = useLocalStorageSelect('instanceID', client.instanceIDs);

    const [gameIndex, setGameIndex] = useState(0);
    const gamesData = useGames(client?.gameIDs);
    const games = gamesData?.slice().sort(compareGamesCatalogOrder);

    const saveLocalSettings = () => {
        if (!isFullscreen) {
            toggleFullscreen();
        }
        const screenID = client.getScreenForDevice(currentValue);
        const gameID = games?.[gameIndex].id;

        if (screenID && gameID) {
            // ClientDatabase.changeGame(client.id, screenID, gameID).then(() => {
            navigate(`/app/${client.id}?device=${currentValue}&game=${gameID}`);
            // });
        }
    };

    return (
        <div className={styles.content}>
            <span className="tag">{client?.name}</span>
            <h1>
                {t('home.configureUnbox')} <span className="emph">Unbox</span> ✌️
            </h1>
            <div className={styles.startContainer}>
                {currentOption && (
                    <Button
                        className={classNames(styles.startButton, styles.buttonAnimation)}
                        icon={<IoPlayOutline />}
                        onClick={() => saveLocalSettings()}
                    >
                        {t('home.start')} <span className={styles.instanceID}>{currentOption.label}</span>
                    </Button>
                )}
            </div>
            <div>
                <h3>{t('home.selectBox')}</h3>
                <div>
                    <Select
                        id="instanceID"
                        containerStyle={{ width: '200px' }}
                        value={currentOption}
                        options={options}
                        onChange={onChange}
                    />
                </div>
            </div>
            <div>
                <h3>{t('home.selectGame')}</h3>
                <div>
                    <div className={styles.cards}>
                        {games ? (
                            games.map((game, index) => (
                                <GameCard
                                    key={game.id}
                                    name={game.name}
                                    src={game.icon}
                                    onSelectGame={() => setGameIndex(index)}
                                    selected={index === gameIndex}
                                />
                            ))
                        ) : (
                            <LoadingPage>{t('home.loadingGames')}</LoadingPage>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
